import { useQuery } from '@tanstack/react-query'
import {
  getSubstationsGeoJSON,
  getFeedersGeoJSON,
  getTransformersGeoJSON,
  getMetersGeoJSON,
  getConductorsGeoJSON,
  getSecondaryGeoJSON,
  getDevicesGeoJSON,
  type BBox,
} from '../api/map.ts'
import { getFaultCurrentGeoJSON } from '../api/faultAnalysis.ts'
import { isDegenerateBBox } from '../utils/bbox.ts'

export function useSubstationsLayer() {
  return useQuery({
    queryKey: ['map', 'substations'],
    queryFn: getSubstationsGeoJSON,
    staleTime: 60_000,
  })
}

export function useFeedersLayer() {
  return useQuery({
    queryKey: ['map', 'feeders'],
    queryFn: getFeedersGeoJSON,
    staleTime: 60_000,
  })
}

export function useTransformersLayer(
  params?: { feeder_id?: number; status?: string },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['map', 'transformers', params],
    queryFn: () => getTransformersGeoJSON(params),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  })
}

export function useMetersLayer(params?: { bbox?: BBox; status?: string }) {
  return useQuery({
    queryKey: ['map', 'meters', params],
    queryFn: () => getMetersGeoJSON(params),
    staleTime: 30_000,
    // Only fetch with a real, non-degenerate bounding box: Leaflet reports
    // zero-area bounds while the container is still sizing, and a zero-area
    // meters query is wasted load for an empty layer (D3).
    enabled: !!params?.bbox && !isDegenerateBBox(params.bbox),
  })
}

export function useConductorsLayer(params?: { feeder_id?: number }) {
  return useQuery({
    queryKey: ['map', 'conductors', params],
    queryFn: () => getConductorsGeoJSON(params),
    staleTime: 60_000,
  })
}

export function useSecondaryLayer(params?: {
  bbox?: BBox
  circuit?: string
  segment_type?: string
  feeder_id?: number
  include_connectivity?: boolean
}) {
  return useQuery({
    queryKey: ['map', 'secondary', params],
    queryFn: () => getSecondaryGeoJSON(params),
    staleTime: 30_000,
    enabled: !!params?.bbox, // Large layer — only fetch when we have a bounding box
  })
}

export function useDevicesLayer(
  params?: { feeder_id?: number },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['map', 'devices', params],
    queryFn: () => getDevicesGeoJSON(params),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}

export function useFaultCurrentLayer(feederId: number | null) {
  return useQuery({
    queryKey: ['map', 'fault-current', feederId],
    queryFn: () => getFaultCurrentGeoJSON(feederId!),
    staleTime: 120_000,
    enabled: feederId !== null,
  })
}
